import { writable, get } from 'svelte/store'
import { navigate } from 'sly-svelte-location-router'
import { toast } from './toast'
import { apiGet, apiPost } from '../api'
import { loadBrowserState } from './ui'
import { loadUserSettings } from './user'
import { completeCLIAuth } from '@lib/sdm'

interface AuthState {
  isAuthenticated: boolean
  id: string | null
  email: string | null
  name: string | null
  phone: string | null
  createdBy: string | null
  type: string
  isAdmin: boolean
  isLoading: boolean
  settings: any | null
}

export const authState = writable<AuthState>({
  isAuthenticated: false,
  id: null,
  email: null,
  name: null,
  phone: null,
  createdBy: null,
  type: 'user',
  isAdmin: false,
  isLoading: true,
  settings: null
})

const checkAuthStatus = async (): Promise<AuthState> => {
  try {
    const data = await apiGet('/api/me')
    const userType = data.type || 'user'
    return {
      isAuthenticated: true,
      id: data.id,
      email: data.email,
      name: data.name,
      phone: data.phone,
      createdBy: data.created_by,
      type: userType,
      isAdmin: userType === 'admin',
      settings: data.settings || null,
      isLoading: false
    }
  } catch (err) {
    return {
      isAuthenticated: false,
      id: null,
      email: null,
      name: null,
      phone: null,
      createdBy: null,
      type: 'user',
      isAdmin: false,
      settings: null,
      isLoading: false
    }
  }
}

export const authInitialized = checkAuthStatus().then(state => {
  authState.set(state)
  if (state.settings) {
    loadUserSettings(state.settings)
    loadBrowserState(state.settings)
  }
  return state
})

let refreshTimer: number | null = null

function scheduleTokenRefresh() {
  if (refreshTimer) {
    clearTimeout(refreshTimer)
  }

  refreshTimer = window.setTimeout(async () => {
    const state = get(authState)
    if (state.isAuthenticated) {
      try {
        await apiPost('/api/auth/refresh')
        scheduleTokenRefresh()
      } catch (err) {
        console.error('Proactive token refresh failed:', err)
      }
    }
  }, 50 * 60 * 1000)
}

authState.subscribe(state => {
  if (state.isAuthenticated && !state.isLoading) {
    scheduleTokenRefresh()
  } else if (refreshTimer) {
    clearTimeout(refreshTimer)
    refreshTimer = null
  }
})

export const login = (email: string, id: string = '', name: string = '', phone: string = '', createdBy: string | null = null, type: string = 'user', settings: any = null) => {
  authState.set({ isAuthenticated: true, id, email, name, phone, createdBy, type, isAdmin: type === 'admin', settings, isLoading: false })
  if (settings) {
    loadUserSettings(settings)
    loadBrowserState(settings)
  }
  toast.success('Successfully logged in')
  navigate('/main')
}

export const logout = async () => {
  try {
    await apiPost('/api/auth/logout', undefined, { skipAuthRedirect: true })
    toast.success('Successfully logged out')
  } catch (err) {
    console.error('Logout API error:', err)
    toast.error('Logout failed')
  }

  authState.set({ isAuthenticated: false, id: null, email: null, name: null, phone: null, createdBy: null, type: 'user', isAdmin: false, settings: null, isLoading: false })
  loadUserSettings(null)

  setTimeout(() => {
    navigate('/login')
  }, 100)
}

export const clearAuth = () => {
  authState.set({
    isAuthenticated: false,
    id: null,
    email: null,
    name: null,
    phone: null,
    createdBy: null,
    type: 'user',
    isAdmin: false,
    settings: null,
    isLoading: false
  })

  loadUserSettings(null)

  document.cookie = 'sdm_access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'
  document.cookie = 'sdm_refresh_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;'

  toast.error('Your session has expired. Please log in again.')
}

export const handleCLIAuth = async (code: string): Promise<void> => {
  try {
    const response = await apiPost<{
      sdm_access_token: string
      sdm_refresh_token: string
    }>(`/api/auth/cli?code=${encodeURIComponent(code)}`)

    await completeCLIAuth(response.sdm_access_token, response.sdm_refresh_token)

    toast.success('CLI authenticated successfully')
  } catch (err: any) {
    console.error('CLI authentication failed:', err)
    toast.error('CLI authentication failed')
    throw err
  }
}